import React from "react";
import { Box, Stack, Text } from "@chakra-ui/react";
import { PrincipalOverviewProps } from "./PrincipalOverview.types";
import { Helmet } from "react-helmet";
import {
  dateToDate,
  dateToMonYYYYUTC,
  dateToString,
} from "../../../../utils/dates";
import { BookIcon, BriefcaseIcon } from "../../../../icons";
import { VeriHeading } from "../../VeriHeading";
import { htmlToText } from "../../../../utils";
import useGetDataOrDefaults from "../../../../hooks/data/useGetDataOrDefaults";
import usePrincipalModel from "../../../../api/hooks/models/usePrincipalModel";
import { BackgroundCheckItem } from "./BackgroundCheckItem";
import { PrincipalTeamItem } from "./PrincipalTeamItem";
import { JobLicenseItem } from "./JobLicenseItem";
import { PrincipalHistoryModel } from "../../../../api/models";

const sortByStartDate = (
  a: PrincipalHistoryModel,
  b: PrincipalHistoryModel,
) => {
  if ((a.startDate || 0) > (b.startDate || 0)) {
    return -1;
  }
  if ((a.startDate || 0) < (b.startDate || 0)) {
    return 1;
  }
  return 0;
};

export const PrincipalOverview: React.FC<PrincipalOverviewProps> = ({
  principalId,
  principal: dataPrincipal,
  ...rest
}) => {
  const principalModel = usePrincipalModel({ usePlacement: false });

  const { data: principal } = useGetDataOrDefaults(dataPrincipal, async () =>
    principalId ? (await principalModel.getOne(principalId)).data : undefined,
  );

  if (!principal) {
    return <></>;
  }
  const jobs = principal.principalHistories?.filter(
    (principalHistory) => principalHistory.historyType === "Employment",
  );

  const educations = principal.principalHistories?.filter(
    (principalHistory) => principalHistory.historyType === "Education",
  );

  const sortedBackgroundChecks =
    ((principal.backgroundchecks && principal.backgroundchecks) || []).sort(
      (a, b) => {
        if ((a.checkedOn || 0) < (b.checkedOn || 0)) return 1;
        if ((a.checkedOn || 0) > (b.checkedOn || 0)) return -1;
        return 0;
      },
    ) || false;

  const backgroundChecks = sortedBackgroundChecks[0];

  return (
    <Box {...rest}>
      <Helmet>
        <title>{`${principal.name} - ${principal.manager?.name} - Verivest`}</title>
        <meta
          name="description"
          content={htmlToText(principal?.descriptionHtml || "")}
        />
        {principal.imgUrl && (
          <meta property="og:image" content={principal.imgUrl} />
        )}
      </Helmet>
      <PrincipalTeamItem mt={8} principal={principal} />
      {backgroundChecks && (
        <Box mt="24px">
          <VeriHeading textStyle="h5normal">Background checks</VeriHeading>
          <Text mt="4px" mb="24px" color="text.grey" textStyle="bodyRegular">
            {`Current as of ${dateToString(
              new Date(Number(backgroundChecks.checkedOn) * 1000),
            )}`}
          </Text>
          <Box border="1px solid" borderColor="border.input" borderRadius={4}>
            {backgroundChecks.backgroundcheckitems &&
              backgroundChecks.backgroundcheckitems
                //TODO This sorting was by name, but the API doesn't return the name
                // Should sort for something else?
                //.sort(compareBackgroundCheckItemName)
                .map((item, i, items) => {
                  const isLast = i === items.length - 1;
                  return (
                    <BackgroundCheckItem
                      key={"bci-" + item.id}
                      borderTopRightRadius={i === 0 ? "4px" : undefined}
                      borderTopLeftRadius={i === 0 ? "4px" : undefined}
                      borderBottomLeftRadius={isLast ? "4px" : undefined}
                      borderBottomRightRadius={isLast ? "4px" : undefined}
                      title={item.backgroundCheckType}
                      text={item.total}
                      years={Number(item.years)}
                      notes={String(item.notes || "")}
                      borderBottomWidth={isLast ? "0px" : "1px"}
                    />
                  );
                })}
          </Box>
        </Box>
      )}
      {jobs && jobs.length > 0 && (
        <Box mt="32px">
          <VeriHeading textStyle="h5normal">Employment history</VeriHeading>
          {jobs.sort(sortByStartDate).map((h, i) => (
            <JobLicenseItem
              icon={BriefcaseIcon}
              key={h.id}
              bg={i % 2 === 0 ? "bg.white" : "bg.grey"}
              title={h.organizationName}
              subtitle={h.title}
              date={`${dateToMonYYYYUTC(
                isNaN(Number(h.startDate))
                  ? dateToDate(String(h.startDate))
                  : new Date(Number(h.startDate) * 1000),
              )} - ${
                h.endDate
                  ? dateToMonYYYYUTC(
                      isNaN(Number(h.endDate))
                        ? dateToDate(h.endDate)
                        : new Date(Number(h.endDate) * 1000),
                    )
                  : "Present"
              }`}
            />
          ))}
        </Box>
      )}
      {educations && educations.length > 0 && (
        <Box mt="32px">
          <VeriHeading textStyle="h5normal">Education & licenses</VeriHeading>
          <Stack spacing="24px">
            {educations.sort(sortByStartDate).map((h, i) => (
              <JobLicenseItem
                icon={BookIcon}
                key={h.id}
                bg={i % 2 === 0 ? "bg.white" : "bg.grey"}
                title={h.organizationName}
                subtitle={h.title}
                date={
                  h.startDate
                    ? `${dateToMonYYYYUTC(
                        isNaN(Number(h.startDate))
                          ? dateToDate(h.startDate)
                          : new Date(Number(h.startDate) * 1000),
                      )} - ${
                        h.endDate
                          ? dateToMonYYYYUTC(
                              isNaN(Number(h.endDate))
                                ? dateToDate(h.endDate)
                                : new Date(Number(h.endDate) * 1000),
                            )
                          : "Present"
                      }`
                    : ""
                }
              />
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
};
